import { flattenSectionTree, type TreeSection } from './sectionTree';

/** Minimal item shape needed to flatten into the Sections-view order. */
export interface FlattenableItem {
  itemId: string;
  sectionId: string | null;
  sortOrder: number;
}

/**
 * Order top-level items to MATCH the Sections view top-to-bottom: a pre-order
 * walk of the section tree (each level by `sortOrder`) emitting each section's
 * items (by `sortOrder`), then the unsorted (section-less) items — exactly how
 * `ListDetailPage` renders the section cards followed by the Unsorted bucket.
 *
 * This drives the flat "List" view, which has NO order of its own: it's a live
 * projection of the section order. An item whose section is missing from the
 * tree (e.g. a since-deleted section) is treated as unsorted so nothing drops.
 */
export function flattenSectionItemOrder<S extends TreeSection, I extends FlattenableItem>(
  sections: S[],
  items: I[],
): I[] {
  const bySection = new Map<string | null, I[]>();
  for (const it of items) {
    const key = it.sectionId ?? null;
    const arr = bySection.get(key);
    if (arr) arr.push(it);
    else bySection.set(key, [it]);
  }
  for (const arr of bySection.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);

  const out: I[] = [];
  const emitted = new Set<string>();
  for (const { section } of flattenSectionTree(sections)) {
    const arr = bySection.get(section.sectionId);
    if (arr) out.push(...arr);
    emitted.add(section.sectionId);
  }
  // The Unsorted bucket (null) plus any orphan (item whose section isn't in the
  // tree) goes last, stable by sortOrder — mirrors the Unsorted bucket position.
  const tail: I[] = [];
  for (const [key, arr] of bySection) {
    if (key === null || !emitted.has(key)) tail.push(...arr);
  }
  tail.sort((a, b) => a.sortOrder - b.sortOrder);
  out.push(...tail);
  return out;
}
